import { sessionError } from './sessionTransition'

const CHANNEL = 'promo-session-generation'
const LOCK = 'promo-session-mutation'
const validEvent = value => value && /^[a-f0-9]{32}$/.test(value.generation) &&
  ['changing', 'committed', 'failed'].includes(value.status) && Object.keys(value).length === 2

// Web Locks owns transport AND publication. Events contain no identity or marker.
// Construction is explicit; importing this module registers nothing.
export function createSessionMutation(browser, session) {
  let channel = null
  let disposed = false
  let lastEvent = null
  let holdingLock = false
  let tail = Promise.resolve()
  const waiting = new Set()
  let durable = false
  const readEvent = () => {
    try {
      const raw = browser.localStorage.getItem(CHANNEL)
      const event = raw ? JSON.parse(raw) : null
      return validEvent(event) ? JSON.stringify(event) : null
    } catch (_) { durable = false; return null }
  }
  try {
    // Probe only our key; do not touch theme or account data.
    const stored = browser.localStorage.getItem(CHANNEL)
    if (stored === null) browser.localStorage.setItem(CHANNEL, '{}')
    durable = true
    lastEvent = readEvent()
  } catch (_) {}
  const checkGeneration = (resumed = false) => {
    const current = readEvent()
    if ((resumed && !durable && !holdingLock) || (durable && current !== lastEvent)) {
      lastEvent = current
      session.invalidate()
    }
  }
  const receive = (event, persisted = true) => {
    if (disposed) return
    // Notifications are hints, not an ordered log. Re-read the current durable
    // record even for deletion/clear events; newValue may describe a predecessor.
    const current = readEvent()
    if (durable && persisted) {
      if (current !== lastEvent) { lastEvent = current; session.invalidate() }
      return
    }
    if (!validEvent(event)) return
    // BC-only has no global sequence. While we actually own the exclusive lock,
    // every other compliant publisher necessarily preceded us. Do not revoke
    // the owner or move its baseline backwards because delivery was delayed.
    // Outside the lock unknown generations remain fail-closed (not "newer").
    if (holdingLock) return
    const serialized = JSON.stringify(event)
    if (serialized === lastEvent) return
    lastEvent = serialized
    session.invalidate()
    // A queued redirect keeps its private entry; no reload and no cookie clear.
  }
  try {
    channel = new browser.BroadcastChannel(CHANNEL)
    channel.onmessage = ({ data }) => {
      if (!data) return
      // The publisher may have lost storage while this tab can still read it.
      // Only a confirmed durable write makes that shared record authoritative
      // for this notification. Older/unknown envelopes are conservative hints.
      receive({ generation: data.generation, status: data.status }, data.persisted === true)
    }
  } catch (_) { channel = null }
  const storage = event => {
    if (event.key !== CHANNEL && event.key !== null) return
    let value = null
    try { value = JSON.parse(event.newValue) } catch (_) {}
    receive(value)
  }
  const removePeerCheck = session.setPeerCheck(checkGeneration)
  const resume = () => {
    if (browser.document.visibilityState === 'hidden') return
    checkGeneration(true)
    session.check()
  }
  browser.addEventListener('storage', storage)
  browser.addEventListener('focus', resume)
  browser.addEventListener('pageshow', resume)
  browser.document.addEventListener('visibilitychange', resume)
  const publish = (generation, status) => {
    const event = { generation, status }
    lastEvent = JSON.stringify(event)
    let sent = false
    try { browser.localStorage.setItem(CHANNEL, lastEvent); durable = true; sent = true } catch (_) { durable = false }
    try { if (channel) { channel.postMessage({ ...event, persisted: sent }); sent = true } } catch (_) {}
    return sent
  }
  return {
    async run({ kind, entry, operation, signal, waitMs = 15000 }) {
      let input = null
      let timer
      let controller
      let abort
      let stopWaiting = () => {}
      let started = false
      let lockManager = null
      let coordinated = false
      try {
        try { lockManager = browser.navigator.locks } catch (_) {}
        const locked = lockManager && typeof lockManager.request === 'function'
        coordinated = locked && (channel || durable) && browser.AbortController && browser.crypto &&
          typeof browser.crypto.getRandomValues === 'function'
        if (disposed || (signal && signal.aborted)) throw sessionError('SESSION_CANCELLED')
        if (!['redirect', 'login', 'signin', 'logout'].includes(kind) ||
            (!!entry !== (kind === 'redirect'))) throw sessionError('ENTRY_UNAVAILABLE')
        if (kind === 'redirect' && !coordinated) throw sessionError('COORDINATION_UNAVAILABLE')
        if (entry && !['ready', 'invalid'].includes(entry.status)) throw sessionError('ENTRY_UNAVAILABLE')
        if (browser.AbortController) {
          controller = new browser.AbortController()
          abort = () => controller.abort()
          waiting.add(abort)
          if (signal) signal.addEventListener('abort', abort)
          timer = browser.setTimeout(abort, waitMs)
        }
        const execute = async () => {
          started = true
          browser.clearTimeout(timer)
          if (disposed || (controller && controller.signal.aborted) || (signal && signal.aborted)) throw sessionError('SESSION_CANCELLED')
          input = entry ? entry.consume() : { previousMarker: null, auth: null }
          if (!input) throw sessionError('ENTRY_CONSUMED')
          const bytes = new Uint8Array(16)
          let generation = null
          if (coordinated) {
            browser.crypto.getRandomValues(bytes)
            generation = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('')
          }
          const attempt = session.begin(input.previousMarker)
          input.previousMarker = null
          try {
            if (coordinated && !publish(generation, 'changing')) throw sessionError('COORDINATION_UNAVAILABLE')
            // Invalid links request credential-free invalidation, never a bad cipher POST.
            const result = await operation({ attempt, auth: input.auth,
              invalidateOnly: !!entry && entry.status === 'invalid', signal: controller ? controller.signal : signal })
            input.auth = null
            if (disposed || (controller && controller.signal.aborted) || (signal && signal.aborted)) throw sessionError('SESSION_CANCELLED')
            if (!session.owns(attempt)) throw sessionError('SESSION_OBSOLETE')
            const loggingOut = kind === 'logout' || (entry && entry.status === 'invalid')
            if (loggingOut && (!result || result.userLogged !== false)) throw sessionError('SESSION_INVALID_RESPONSE')
            const marker = loggingOut ? null : result && result.auth_token
            // Announce before synchronous publication so notification failure cannot
            // leave a published but uncoordinated client account.
            if (coordinated && !publish(generation, 'committed')) throw sessionError('COORDINATION_UNAVAILABLE')
            session.commit(attempt, marker)
            return { authenticated: marker !== null }
          } catch (error) {
            if (kind === 'redirect' && error && error.code === 'REDIRECT_AUTH_DISABLED' &&
                session.restoreDisabledRedirect(attempt)) {
              if (coordinated) publish(generation, 'failed')
              return { authenticated: true, restored: true }
            }
            session.fail(attempt)
            if (coordinated) publish(generation, 'failed')
            throw sessionError(error && /^SESSION_|^COORDINATION_/.test(error.code) ? error.code : 'SESSION_FAILED', error && error.status)
          } finally { input.auth = input.previousMarker = null }
        }
        if (locked && controller) {
          try {
            return await lockManager.request(LOCK, { mode: 'exclusive', signal: controller.signal }, async () => {
              holdingLock = true
              try { return await execute() }
              finally { holdingLock = false }
            })
          }
          catch (error) {
            // A rejected acquisition is different from a rejected transport. Never
            // retry an operation that entered the lock (it may have changed SID).
            const unavailable = !started && error && ['SecurityError', 'NotSupportedError'].includes(error.name)
            if (!unavailable) throw error
            if (kind === 'redirect') throw sessionError('COORDINATION_UNAVAILABLE')
            coordinated = false
          }
        }
        // Legacy fallback is intentionally same-tab only; never a storage spinlock.
        const next = tail.then(execute)
        tail = next.catch(() => {})
        if (!controller) return await next
        const cancelledWait = new Promise((resolve, reject) => {
          const cancelWait = () => { if (!started) reject(sessionError('SESSION_CANCELLED')) }
          controller.signal.addEventListener('abort', cancelWait)
          stopWaiting = () => controller.signal.removeEventListener('abort', cancelWait)
          if (controller.signal.aborted) cancelWait()
        })
        return await Promise.race([next, cancelledWait])
      } catch (error) {
        throw sessionError(error && /^(SESSION_|COORDINATION_|ENTRY_)/.test(error.code) ? error.code : 'SESSION_CANCELLED', error && error.status)
      } finally {
        browser.clearTimeout(timer)
        stopWaiting()
        if (abort) { waiting.delete(abort); if (signal) signal.removeEventListener('abort', abort) }
        if (input) input.auth = input.previousMarker = null
        if (entry) entry.release()
      }
    },
    dispose() {
      disposed = true
      removePeerCheck()
      waiting.forEach(abort => abort())
      browser.removeEventListener('storage', storage)
      browser.removeEventListener('focus', resume)
      browser.removeEventListener('pageshow', resume)
      browser.document.removeEventListener('visibilitychange', resume)
      if (channel) channel.close()
    },
  }
}
